import { effect, Injectable, NgZone } from '@angular/core';
import { ScriptLoader } from '../../../../features/common/utils/script-loader';
import { createDeferred } from '../../../../features/common/utils';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { DialogService } from './dialog.service';
import { toSignal } from '@angular/core/rxjs-interop';

interface Chatwoot {
  setUser(key: string, value: Record<string, string | undefined>): void;
  toggle(state?: 'open' | 'close'): void;
  toggleBubbleVisibility(state: 'show' | 'hide'): void;
  setCustomAttributes(attrs: Record<string, number | string | Date>): void;
  setConversationCustomAttributes(attrs: Record<string, number | string | Date>): void;
}

@Injectable({ providedIn: 'root' })
export class ChatwootService {
  private readonly script = new ScriptLoader('https://app.chatwoot.com/packs/js/sdk.js');
  private chatwoot = createDeferred<Chatwoot>();

  // Hide the Chatwoot bubble when navigating to a route with `hideChatwootBubble = true`
  private readonly hideForRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        // Get the data from the deepest activated route
        let route = this.route;

        while (route.firstChild) {
          route = route.firstChild;
        }

        return route.snapshot.data['hideChatwootBubble'] ?? false;
      }),
    ),
    { initialValue: false },
  );

  constructor(
    private readonly dialog: DialogService,
    private readonly ngZone: NgZone,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {
    effect(() => {
      const isDialogOpen = this.dialog.isDialogOpen();
      const hideForRoute = this.hideForRoute();

      this.run((chatwoot) => {
        if (isDialogOpen) {
          chatwoot.toggleBubbleVisibility('hide');
        } else {
          chatwoot.toggleBubbleVisibility(hideForRoute ? 'hide' : 'show');
        }
      });
    });
  }

  public hide(): void {
    this.run((chatwoot) => chatwoot.toggleBubbleVisibility('hide'));
  }

  public async init(): Promise<void> {
    const config = environment.chatwoot;

    if (!config) {
      return;
    }

    if (!this.script.isLoaded()) {
      window.addEventListener('chatwoot:ready', () => {
        this.chatwoot.resolve((window as any).$chatwoot);
      });

      this.script.load().then(() => {
        this.ngZone.runOutsideAngular(() => {
          (window as any).chatwootSettings = {
            hideMessageBubble: true,
            type: 'standard',
            launcherTitle: 'Chat with us',
          };

          (window as any).chatwootSDK.run({
            websiteToken: config.token,
            baseUrl: 'https://app.chatwoot.com',
          });
        });
      });
    }
  }

  public isEnabled(): boolean {
    return !!environment.chatwoot;
  }

  public open(): void {
    this.run((chatwoot) => chatwoot.toggle('open'));
  }

  public run(cb: (chatwoot: Chatwoot) => void): void {
    if (this.isEnabled()) {
      this.ngZone.runOutsideAngular(() => {
        this.chatwoot.promise.then(cb);
      });
    }
  }

  public show(): void {
    this.run((chatwoot) => chatwoot.toggleBubbleVisibility('show'));
  }
}
